import { useFormContext, useWatch } from 'react-hook-form';
import { ApprovalTableCellInput } from '@/components/Approval/@components/ApprovalFormTable';
import MortgageFeeTables, {
  type MortgageFeeCellKey,
} from '@/components/Approval/@components/MortgageFee';
import { formatAmountInput } from '@/utils/approval/expensePreApproval';
import {
  calcMortgageFeeTotals,
  formatMortgageFeeAmount,
  type MortgageFeeAmountKey,
  type MortgageFeeFormState,
} from '@/utils/approval/mortgageFee';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const AMOUNT_KEYS = new Set<MortgageFeeCellKey>([
  'collateralValuationAmount',
  'collateralPriorLienAmount',
  'collateralMaximumSecuredAmount',
  'collateralNetFeeAmount',
  'collateralVatAmount',
] satisfies MortgageFeeAmountKey[]);

const MAX_LENGTH: Partial<Record<MortgageFeeCellKey, number>> = {
  collateralAddress: 500,
};

/** (구)근저당설정비용 지급(81) — 매장정보·담보내용·설정비용 직접 입력, 총비용·본사 부담 자동 계산 */
const MortgageFeeField = () => {
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'mortgageFee' });

  if (!state) return null;

  const totals = calcMortgageFeeTotals(state);

  const update = (key: keyof MortgageFeeFormState, value: string) => {
    const current = getValues('mortgageFee');
    if (!current) return;
    setValue(
      'mortgageFee',
      { ...current, [key]: value },
      { shouldDirty: true },
    );
  };

  const renderCell = (key: MortgageFeeCellKey) => {
    if (
      key === 'collateralTotalFeeAmount' ||
      key === 'collateralHqBurdenAmount'
    ) {
      const value =
        key === 'collateralTotalFeeAmount' ? totals?.total : totals?.hqBurden;
      return totals ? formatMortgageFeeAmount(value) : '0';
    }
    const isAmount = AMOUNT_KEYS.has(key);
    return (
      <ApprovalTableCellInput
        name={key}
        className={isAmount ? 'align-right' : undefined}
        value={state[key]}
        inputProps={{
          maxLength: MAX_LENGTH[key] ?? 100,
          inputMode: isAmount ? 'decimal' : undefined,
        }}
        onChange={(e) =>
          update(
            key,
            isAmount ? formatAmountInput(e.target.value) : e.target.value,
          )
        }
      />
    );
  };

  return (
    <FieldBlock>
      <MortgageFeeTables
        editable
        hasCalculated={!!totals}
        renderCell={renderCell}
      />
    </FieldBlock>
  );
};

export default MortgageFeeField;
